import type { ReactElement } from 'react';
import type { DeploymentRegion } from '@nest-react/domain';
import { useTranslation } from 'react-i18next';

import { useRegion } from '~/app/region';
import { BrandLogo } from '~/components/ui/BrandLogo';

const choices: {
  region: DeploymentRegion;
  labelKey: string;
  detailKey: string;
  code: string;
}[] = [
  {
    region: 'RU',
    labelKey: 'region.russia',
    detailKey: 'region.russiaDetail',
    code: 'RU',
  },
  {
    region: 'GLOBAL',
    labelKey: 'region.global',
    detailKey: 'region.globalDetail',
    code: 'GL',
  },
];

export const RegionSelectionPage = (): ReactElement => {
  const { t } = useTranslation('auth');
  const { selectRegion } = useRegion();
  return (
    <main className="grid min-h-screen place-items-center bg-surface px-5 py-10 text-foreground">
      <section className="w-full max-w-3xl">
        <BrandLogo />
        <div className="mt-14 max-w-xl">
          <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-primary">
            {t('region.boundary')}
          </p>
          <h1 className="mt-3 text-4xl font-semibold tracking-[-0.045em] sm:text-5xl">
            {t('region.title')}
          </h1>
          <p className="mt-4 leading-7 text-muted">{t('region.description')}</p>
        </div>
        <div className="mt-10 grid gap-4 sm:grid-cols-2">
          {choices.map(choice => (
            <button
              className="group relative min-h-48 overflow-hidden rounded-3xl border border-border bg-elevated p-6 text-left transition hover:-translate-y-0.5 hover:border-primary/40 focus-visible:outline-2 focus-visible:outline-primary motion-reduce:transform-none"
              key={choice.region}
              onClick={() => selectRegion(choice.region)}
              type="button"
            >
              <span className="absolute right-5 top-4 font-mono text-5xl font-semibold tracking-[-0.08em] text-foreground/[0.06] transition group-hover:text-primary/10">
                {choice.code}
              </span>
              <strong className="relative block text-xl">
                {t(choice.labelKey)}
              </strong>
              <span className="relative mt-14 block max-w-xs text-sm leading-6 text-muted">
                {t(choice.detailKey)}
              </span>
              <span className="relative mt-4 block text-xs font-semibold text-primary">
                {t('region.continue', { region: choice.region })}
              </span>
            </button>
          ))}
        </div>
      </section>
    </main>
  );
};

export const RegionConnectionPage = (): ReactElement => {
  const { t } = useTranslation('auth');
  const { region, state, error, retry, resetRegion } = useRegion();
  if (state === 'verifying') {
    return (
      <main className="grid min-h-screen place-items-center bg-surface text-foreground">
        <p aria-live="polite">{t('region.connecting', { region })}</p>
      </main>
    );
  }
  return (
    <main className="grid min-h-screen place-items-center bg-surface p-6 text-center text-foreground">
      <section className="max-w-md rounded-3xl border border-border bg-elevated p-8">
        <p className="font-mono text-xs uppercase tracking-widest text-primary">
          {t('region.unavailable', { region })}
        </p>
        <h1 className="mt-3 text-2xl font-semibold">{t('region.blocked')}</h1>
        <p className="mt-3 text-sm leading-6 text-muted">
          {error || t('region.verifyFailed')} {t('region.noLogin')}
        </p>
        <div className="mt-6 flex justify-center gap-3">
          <button
            className="rounded-xl bg-primary px-4 py-2 font-semibold text-white"
            onClick={retry}
            type="button"
          >
            {t('region.retry')}
          </button>
          <button
            className="rounded-xl border border-border px-4 py-2 font-semibold"
            onClick={() => void resetRegion()}
            type="button"
          >
            {t('region.choose')}
          </button>
        </div>
      </section>
    </main>
  );
};
